import type { datadogLogs as DatadogLogsApi } from "@datadog/browser-logs";
import type { datadogRum as DatadogRumApi } from "@datadog/browser-rum";

/**
 * Consent-gated Datadog runtime (RUM + browser logs).
 *
 * The SDKs are only downloaded and initialized after the visitor accepts
 * analytics, only in the browser, and only on Vercel production/preview
 * deployments (never in development, tests or E2E). `lib/analytics` and
 * `lib/logger` reach Datadog exclusively through `getActiveRum()` /
 * `getActiveLogs()`, so they are no-ops without consent.
 */
type DatadogRum = typeof DatadogRumApi;
type DatadogLogs = typeof DatadogLogsApi;

export interface DatadogConfig {
  applicationId: string;
  clientToken: string;
  site: string;
  service: string;
  env: "production" | "preview";
  version?: string;
}

/** Public client-side identifiers (not secrets); override via env. */
const DEFAULTS = {
  applicationId: "1134d800-a193-4d20-b0fb-2f24a8bcc100",
  clientToken: "pub4ed660915bf3080185be5903321e2f00",
  site: "us3.datadoghq.com",
  service: "cage-mdb",
} as const;

/** Datadog's session cookie (and localStorage key in fallback mode). */
export const DATADOG_SESSION_KEY = "_dd_s";

/**
 * Datadog settings for this deployment, or `null` when Datadog must stay off.
 * `process.env.NEXT_PUBLIC_*` must be referenced literally to be inlined into
 * the client bundle. Setting the application id or client token to an empty
 * string disables Datadog.
 */
export function getDatadogConfig(): DatadogConfig | null {
  const env = process.env.NEXT_PUBLIC_VERCEL_ENV;

  if (env !== "production" && env !== "preview") return null;

  const applicationId =
    process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID ?? DEFAULTS.applicationId;
  const clientToken =
    process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN ?? DEFAULTS.clientToken;

  if (!applicationId || !clientToken) return null;

  return {
    applicationId,
    clientToken,
    site: process.env.NEXT_PUBLIC_DATADOG_SITE || DEFAULTS.site,
    service: process.env.NEXT_PUBLIC_DATADOG_SERVICE || DEFAULTS.service,
    env,
    version: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA || undefined,
  };
}

interface Runtime {
  rum: DatadogRum | null;
  logs: DatadogLogs | null;
  initialized: boolean;
  active: boolean;
  /** Latest intent; guards against consent changing while the SDK loads. */
  wanted: boolean;
  userId: string | null;
}

const runtime: Runtime = {
  rum: null,
  logs: null,
  initialized: false,
  active: false,
  wanted: false,
  userId: null,
};

/** Start (or resume) Datadog. Resolves to whether it is running. */
export async function enableDatadog(
  config: DatadogConfig | null = getDatadogConfig(),
): Promise<boolean> {
  if (typeof window === "undefined" || !config) return false;

  runtime.wanted = true;
  if (runtime.active) return true;

  try {
    const [{ datadogRum }, { datadogLogs }] = await Promise.all([
      import("@datadog/browser-rum"),
      import("@datadog/browser-logs"),
    ]);

    // Consent was withdrawn (or already granted again) while loading.
    if (!runtime.wanted) return false;
    if (runtime.active) return true;

    if (runtime.initialized) {
      datadogRum.setTrackingConsent("granted");
      datadogLogs.setTrackingConsent("granted");
    } else {
      const shared = {
        clientToken: config.clientToken,
        site: config.site,
        service: config.service,
        env: config.env,
        version: config.version,
        trackingConsent: "granted" as const,
        // No long-lived anonymous id: the session cookie expires with the session.
        trackAnonymousUser: false,
      };

      datadogRum.init({
        ...shared,
        applicationId: config.applicationId,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 10,
        defaultPrivacyLevel: "mask-user-input",
        enablePrivacyForActionName: true,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        trackBfcacheViews: true,
      });
      datadogLogs.init({
        ...shared,
        forwardErrorsToLogs: true,
        sessionSampleRate: 100,
      });
      runtime.initialized = true;
    }

    runtime.rum = datadogRum;
    runtime.logs = datadogLogs;
    runtime.active = true;
    if (runtime.userId) datadogRum.setUser({ id: runtime.userId });

    return true;
  } catch {
    // Analytics must never break the page (e.g. blocked by an ad blocker).
    return false;
  }
}

/** Delete Datadog's session cookie on this host and its parent domains. */
export function removeDatadogStorage(): void {
  if (typeof document === "undefined") return;

  const expired = `${DATADOG_SESSION_KEY}=; Max-Age=0; Path=/`;
  const labels = window.location.hostname.split(".");

  document.cookie = expired;
  for (let index = 0; index < labels.length - 1; index++) {
    document.cookie = `${expired}; Domain=${labels.slice(index).join(".")}`;
  }

  try {
    window.localStorage.removeItem(DATADOG_SESSION_KEY);
  } catch {
    // Storage can be unavailable (privacy mode); nothing to clean up then.
  }
}

/**
 * Stop collecting: revoke tracking consent (no new sessions), end the
 * current session, stop log forwarding and remove the session cookie.
 */
export function disableDatadog(): void {
  runtime.wanted = false;

  if (runtime.active && runtime.rum && runtime.logs) {
    try {
      runtime.rum.setTrackingConsent("not-granted");
      runtime.rum.stopSession();
      runtime.logs.setTrackingConsent("not-granted");
    } catch {
      // Ignore SDK errors; the storage cleanup below still runs.
    }
  }

  runtime.active = false;
  removeDatadogStorage();
}

export function getActiveRum(): DatadogRum | null {
  return runtime.active ? runtime.rum : null;
}

export function getActiveLogs(): DatadogLogs | null {
  return runtime.active ? runtime.logs : null;
}

/** Associate RUM data with an opaque user id (never a name or email). */
export function setDatadogUser(userId: string): void {
  runtime.userId = userId;
  getActiveRum()?.setUser({ id: userId });
}

export function clearDatadogUser(): void {
  runtime.userId = null;
  getActiveRum()?.clearUser();
}

/** Forget runtime state (tests only). */
export function resetDatadogRuntime(): void {
  Object.assign(runtime, {
    rum: null,
    logs: null,
    initialized: false,
    active: false,
    wanted: false,
    userId: null,
  } satisfies Runtime);
}
